"use client"

import { useEffect, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { EntityTypeProvider } from "@/contexts/entity-type-context"
import { WorkflowProvider, useWorkflow } from "@/context/workflow-context-simple"
import { WorkflowGrid } from "@/components/workflow-grid"
import { ViewSelector } from "@/components/view-selector"
import { useRequestsTabHeaderSlot, useRequestsTabOpenViewManager } from "@/contexts/requests-tab-header-slot-context"
import type { VmsSubTab } from "@/hooks/vms/use-vms-config"
import type { WorkflowStage } from "@/types/workflow"

/**
 * Renders one VMS sub-tab (Requests or Emails).
 *
 * The grid is the same <WorkflowGrid> the rest of the Workflow Hub uses, but
 * with three header customizations:
 *   • Pin dock + Jump-to-Column hidden (no column-pinning use case for VMS).
 *   • headerTitleSlot replaces the default "Requests (n)" with the
 *     Requests/Emails sub-tab selector passed from the parent.
 *   • headerExtraControls slots the Settings button alongside Edit columns /
 *     Filter so the page no longer needs a separate top toolbar.
 *
 * The two nested providers (EntityTypeProvider in controlled mode +
 * WorkflowProvider) scope the entity type and grid state to this sub-tab.
 * The parent (vms/tab.tsx) mounts BOTH sub-tabs at once and toggles the
 * inactive one with display:none — that way each provider keeps its own
 * filters/pagination/selection AND we don't re-fetch configurations / views
 * / assigned-users every time the user flips between Requests and Emails.
 */
export function VmsSubTabContent({
  subTab,
  headerTitleSlot,
  headerExtraControls,
  headerLeadingControls,
  isActiveSubTab = true,
  onTotalCountChange,
  onOpenLinkedRequest,
  pendingFocusId,
  pendingFocusLabel,
  onPendingFocusConsumed,
}: {
  subTab: VmsSubTab
  headerTitleSlot?: ReactNode
  headerExtraControls?: ReactNode
  headerLeadingControls?: ReactNode
  /**
   * Both VMS sub-tabs mount simultaneously (parent toggles visibility with
   * display:none, see vms/tab.tsx) but only the active one should portal
   * its <ViewSelector /> into the workflow-hub top-right header slot —
   * otherwise both portals would compete for the same DOM target. Default
   * true so standalone consumers (storybook, tests) get the portal without
   * having to wire the flag.
   */
  isActiveSubTab?: boolean
  /**
   * Fired whenever the inner grid's totalCount changes — used by the parent
   * VMS tab to keep the "(n)" on the sub-tab pills in sync with the filtered
   * result count instead of the mount-time wildcard total. The entityType is
   * echoed back so the parent can route updates to the correct pill even when
   * both providers are mounted simultaneously and reporting their own counts.
   */
  onTotalCountChange?: (entityType: VmsSubTab["entityType"], count: number) => void
  /**
   * Forwarded to the inner WorkflowGrid so the VmsEmail row's "View Linked
   * Request" kebab action can bubble back to the parent (vms/tab.tsx) which
   * is the one that knows how to switch sub-tabs.
   */
  onOpenLinkedRequest?: (requestId: string, itemTitle: string) => void
  /**
   * When set, the inner WorkflowProvider is scoped to that single id via an
   * OData `id eq '<X>'` temporary filter. The parent uses this to land on a
   * specific VmsRequest after the user clicked "View Linked Request" on an
   * email row. We expose `onPendingFocusConsumed` so the parent can null the
   * id after we've applied it, preventing re-application on subsequent
   * re-renders (the user can clear the filter at any time via the grid's
   * Clear Filters action).
   */
  pendingFocusId?: string | null
  /**
   * Optional friendly label passed alongside `pendingFocusId` so the chip
   * shown above the grid says something the user understands (e.g. the
   * email subject they clicked "View Linked Request" on) rather than the
   * raw OData clause.
   */
  pendingFocusLabel?: string | null
  onPendingFocusConsumed?: () => void
}) {
  const stage = subTab.stage
  if (!stage) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400">No stage configured for {subTab.entityType}</p>
      </div>
    )
  }

  return (
    <EntityTypeProvider value={subTab.entityType}>
      {/* Hand the configuration useVmsConfig already fetched down to the
          provider — without this, the provider's mount effect would re-issue
          the same /configurations?entityType=... request that useVmsConfig
          just resolved (the coalesce cache only catches concurrent calls, and
          this provider only mounts AFTER useVmsConfig settles). */}
      <WorkflowProvider initialConfiguration={subTab.configuration}>
        <div className="flex-1 flex flex-col overflow-hidden">
          <WorkflowGrid
            stage={stage.stageCode as WorkflowStage}
            hidePinDock
            hideJumpToColumn
            headerTitleSlot={headerTitleSlot}
            headerExtraControls={headerExtraControls}
            headerLeadingControls={headerLeadingControls}
            onOpenLinkedRequest={onOpenLinkedRequest}
          />
          {/* ViewSelector portals into the workflow-hub top-right slot when
              this sub-tab is the active one. Both VmsSubTabContent instances
              (Requests + Emails) mount in parallel; gating on isActiveSubTab
              ensures only one portal renders at a time — without the gate
              the inactive sub-tab's portal would clobber the active one's
              dropdown into the shared DOM slot. The ViewSelector lives
              UNDER this WorkflowProvider so useWorkflow() resolves to the
              VMS sub-tab's context (VmsRequest or VmsEmail), not WorkOrder. */}
          {isActiveSubTab && <ViewSelectorHeaderPortal />}
          {onTotalCountChange && (
            <TotalCountReporter entityType={subTab.entityType} onChange={onTotalCountChange} />
          )}
          {pendingFocusId && (
            <PendingFocusApplier
              pendingFocusId={pendingFocusId}
              pendingFocusLabel={pendingFocusLabel}
              onConsumed={onPendingFocusConsumed}
            />
          )}
        </div>
      </WorkflowProvider>
    </EntityTypeProvider>
  )
}

/**
 * Bridge component that lives inside the WorkflowProvider so it can call
 * `applyTemporaryQueryFilter`. When the parent VMS tab arrives with a
 * pendingFocusId (e.g. via "View Linked Request" from the Emails sub-tab),
 * we scope the grid to that single id and ack so the parent clears the
 * pending state. Subsequent changes to filters / stage / etc. clear the
 * temporary filter via the existing reducer plumbing.
 */
function PendingFocusApplier({
  pendingFocusId,
  pendingFocusLabel,
  onConsumed,
}: {
  pendingFocusId: string
  pendingFocusLabel?: string | null
  onConsumed?: () => void
}) {
  const { applyTemporaryQueryFilter } = useWorkflow()
  useEffect(() => {
    if (!pendingFocusId) return
    // Single-quote escape any embedded quotes by doubling per OData rules.
    const escaped = pendingFocusId.replace(/'/g, "''")
    // Friendly label drives the dismissible chip rendered by workflow-grid.
    // Fall back to a generic phrasing so the chip still says something
    // useful even if the caller forgot to pass a label.
    const label = pendingFocusLabel?.trim() || 'Linked record'

    // Completed-state requests stay visible without touching the user's
    // Show Completed toggle: while a temporary query filter is active the
    // grid's data fetch skips the hide-completed base clause entirely (see
    // triggerDataFetch in workflow-grid.tsx). The old approach flipped the
    // persisted toggle via setShowCompleted, which durably rewrote the
    // user's saved view state and never reverted on dismissal.

    // Filter by RequestId + DocumentType rather than search-index `id`. The
    // index `id` is now a SHA256-derived guid (one writer convention shared
    // with gw-sync — see SearchIndexId.Generate) and is no longer equal to
    // the source VmsRequest doc id that callers pass in here. RequestId
    // continues to carry the original VmsRequest Cosmos doc id; pairing it
    // with `DocumentType eq 'VmsRequest'` is the canonical "find this VMS
    // row" lookup in the shared client-request index.
    applyTemporaryQueryFilter(
      `RequestId eq '${escaped}' and DocumentType eq 'VmsRequest'`,
      label,
    )
    onConsumed?.()
  }, [pendingFocusId, pendingFocusLabel, applyTemporaryQueryFilter, onConsumed])
  return null
}

/**
 * Bridge component: lives INSIDE WorkflowProvider so it can read totalCount
 * from the workflow context, and emits it upward via callback. Kept tiny so
 * it doesn't trigger unnecessary re-renders of the grid. Echoes the
 * entityType back to the parent so when both VMS sub-tabs are mounted in
 * parallel, the parent can attribute each reported count to the correct pill.
 */
function TotalCountReporter({
  entityType,
  onChange,
}: {
  entityType: VmsSubTab["entityType"]
  onChange: (entityType: VmsSubTab["entityType"], count: number) => void
}) {
  const { totalCount } = useWorkflow()
  useEffect(() => {
    onChange(entityType, totalCount)
  }, [entityType, totalCount, onChange])
  return null
}

/**
 * Bridge component that lives INSIDE the WorkflowProvider so its rendered
 * <ViewSelector /> can read useWorkflow(), but uses createPortal to land
 * in the workflow-hub-level header slot (next to the kebab and search).
 * Returns null when the slot ref isn't set (e.g. transient state during
 * the workflow-hub mount; also the WorkOrder tab path where the hub
 * renders its own ViewSelector inline). Same pattern used by
 * ClientRequestSubTabContent.
 */
function ViewSelectorHeaderPortal() {
  const slot = useRequestsTabHeaderSlot()
  // Enables the "View Designer" entry on this tab — see ClientRequestSubTabContent's portal.
  const onOpenViewManager = useRequestsTabOpenViewManager()
  if (!slot) return null
  return createPortal(<ViewSelector onOpenViewManager={onOpenViewManager ?? undefined} />, slot)
}
